import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Building2, Inbox, ArrowRight } from "lucide-react";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { PageTransition } from "@/components/common/PageTransition";
import { Button } from "@/components/ui/button";
import { usePageHeading } from "@/components/layout/AppLayout";
import { dashboardApi } from "@/lib/api";
import { formatNumber, timeAgo } from "@/lib/format";
import { TID } from "@/constants/testIds";
import { useNavigate } from "react-router-dom";

export default function Approvals() {
  const navigate = useNavigate();
  usePageHeading("Approvals", "Review pending clinic applications");
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await dashboardApi.pending();
        const normalized = (data || []).map((c) => ({
          ...c,
          id: c.id ?? c.clinicId ?? c.clinic?.id,
          name: c.clinicName ?? c.name ?? c.clinic?.clinicName ?? "Clinic",
          owner: c.owner ?? c.doctorName ?? c.contactPerson ?? "Owner",
          city: c.city ?? "",
          plan: c.planName ?? c.plan ?? c.subscriptionPlan ?? "Trial",
          status: (c.subscriptionStatus ?? c.status ?? "PENDING_APPROVAL").toLowerCase(),
          appliedAt: c.appliedAt ?? c.createdAt ?? new Date().toISOString(),
          image: c.image ?? c.logo ?? "/images/clinic-placeholder.png",
        }));
        setPending(normalized);
      } catch {
        toast.error("Failed to load pending clinics");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <PageTransition className="space-y-8">
      <div data-testid={TID.approvalsPage} />

      <div className="grid gap-6 lg:grid-cols-3">
        <GlassCard className="p-6 lg:col-span-2">
          <div className="flex flex-col gap-2">
            <h1 className="font-heading text-2xl font-semibold tracking-tight">Clinic approvals</h1>
            <p className="text-sm text-muted-foreground">Review pending clinic applications and approve, hold, or reject them.</p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-border/60 p-5">
              <p className="text-sm text-muted-foreground">Pending applications</p>
              <p className="mt-3 text-3xl font-semibold text-foreground">{formatNumber(pending.length)}</p>
            </div>
            <div className="rounded-2xl border border-border/60 p-5">
              <p className="text-sm text-muted-foreground">Review status</p>
              <p className="mt-3 text-3xl font-semibold text-foreground">{loading ? "Loading…" : pending.length ? "Ready" : "All clear"}</p>
            </div>
          </div>
        </GlassCard>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="glass h-44 rounded-2xl" />
          ))}
        </div>
      ) : pending.length === 0 ? (
        <EmptyState icon={Inbox} title="No pending approvals" description="There are no clinic applications awaiting review." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {pending.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
            >
              <GlassCard className="flex h-full flex-col p-5" hover>
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gold/15 text-gold">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="truncate font-semibold">{c.name}</h3>
                      <StatusBadge status={c.status} />
                    </div>
                    <p className="truncate text-sm text-muted-foreground">{c.owner}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {c.city} · {c.plan} · Applied {timeAgo(c.appliedAt)}
                    </p>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/clinics/${c.id}`)}
                    className="flex-1 rounded-full"
                  >
                    View details <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      )}

    </PageTransition>
  );
}
